import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { Play, Pause, Loader2, FastForward, X, Volume2, PictureInPicture2, Headphones } from 'lucide-react';
import { getArticleUrl, slugify } from '../utils/slug';

export const GlobalAudioPlayer: React.FC = () => {
  const { 
    currentArticle, 
    isPlaying, 
    isLoading, 
    currentTime, 
    duration, 
    progress, 
    playbackRate, 
    isPiPSupported,
    isPiPActive,
    togglePiP,
    togglePlay, 
    toggleRate, 
    seekByPercent,
    stop 
  } = useAudioPlayer();

  const location = useLocation();
  const navigate = useNavigate();

  if (!currentArticle) return null;

  // Check if we are currently looking at this exact article's full page
  const path = location.pathname;
  const currentSlug = currentArticle.slug || (currentArticle.title ? slugify(currentArticle.title) : currentArticle.id);
  const isCurrentlyOnArticlePage = 
    path === `/artigo/${currentArticle.id}` || 
    path === `/artigo/${currentSlug}` ||
    path === `/artigos/${currentArticle.id}` || 
    path === `/artigos/${currentSlug}`;

  // If user is already on the article page, the in-page player is visible; don't show the global bottom bar
  if (isCurrentlyOnArticlePage) return null;

  const formatTime = (timeInSeconds: number) => {
    if (!timeInSeconds || isNaN(timeInSeconds) || timeInSeconds <= 0) return "00:00";
    const totalSecs = Math.floor(timeInSeconds);
    const hours = Math.floor(totalSecs / 3600);
    const minutes = Math.floor((totalSecs % 3600) / 60);
    const seconds = totalSecs % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleOpenArticle = () => {
    navigate(getArticleUrl(currentArticle), { state: { article: currentArticle } });
  };

  return (
    <div className="fixed bottom-[4.75rem] md:bottom-6 left-3 right-3 sm:left-auto sm:right-6 sm:w-[400px] z-40 animate-fade-in-up font-sans">
      <div className="bg-[#121212]/95 backdrop-blur-xl border border-stone-800 shadow-[0_10px_40px_rgba(0,0,0,0.8)] rounded-2xl p-3.5 text-stone-200 flex flex-col gap-2.5">
        
        {/* Top row: Track info and controls */}
        <div className="flex items-center justify-between gap-3">
          <div 
            onClick={handleOpenArticle}
            className="flex items-center gap-3 cursor-pointer group flex-1 min-w-0"
            title="Abrir artigo em tela cheia"
          >
            <div className="relative w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border border-primary/40 shadow-md">
              <img 
                src="https://i.postimg.cc/vTfzmk02/Iara-perfil-2.jpg" 
                alt="Iara" 
                className={`w-full h-full object-cover transition-transform duration-500 ${isPlaying ? 'scale-105' : ''}`}
                referrerPolicy="no-referrer"
              />
              {isPlaying && (
                <span className="absolute inset-0 bg-primary/20 animate-pulse pointer-events-none" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-primary font-bold">
                <Volume2 size={12} className={isPlaying ? 'animate-pulse text-red-500' : ''} />
                <span>Narração da Iara</span>
                <span className="text-[9px] text-stone-500 font-normal ml-auto flex items-center gap-0.5">
                  <Headphones size={10} className="text-emerald-500" />
                  Segundo plano
                </span>
              </div>
              <p className="text-xs font-serif font-bold text-stone-100 truncate group-hover:text-primary transition-colors">
                {currentArticle.title}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Picture in Picture Button (allows floating player over other apps) */}
            {isPiPSupported && (
              <button 
                onClick={togglePiP}
                className={`h-7 px-2 rounded-full text-[11px] font-bold transition-all flex items-center gap-1 ${
                  isPiPActive 
                    ? 'bg-primary text-white shadow-[0_0_10px_rgba(239,68,68,0.5)]' 
                    : 'bg-stone-800 hover:bg-stone-700 text-stone-300'
                }`}
                title={isPiPActive ? "Fechar janela flutuante" : "Modo Flutuante (ouvir ao sair do site / usar outros apps)"}
              >
                <PictureInPicture2 size={12} />
                <span className="hidden sm:inline">{isPiPActive ? 'PiP Ativo' : 'Flutuante'}</span>
              </button>
            )}

            <button 
              onClick={toggleRate}
              className="h-7 px-2 rounded-full bg-stone-800 hover:bg-stone-700 text-stone-300 text-[11px] font-bold transition-colors flex items-center gap-0.5"
              title="Velocidade de reprodução"
            >
              <FastForward size={10} />
              {playbackRate}x
            </button>

            <button
              onClick={() => togglePlay()}
              disabled={isLoading}
              className="w-9 h-9 rounded-full bg-primary hover:bg-red-700 text-white flex items-center justify-center transition-all shadow-md active:scale-95"
              title={isPlaying ? "Pausar" : "Tocar"}
            >
              {isLoading ? (
                <Loader2 size={16} className="animate-spin text-white" />
              ) : isPlaying ? (
                <Pause size={16} fill="currentColor" />
              ) : (
                <Play size={16} className="ml-0.5" fill="currentColor" />
              )}
            </button>

            <button
              onClick={stop}
              className="p-1.5 rounded-full text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
              title="Fechar reprodutor"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Progress Bar & Time */}
        <div className="flex items-center gap-2 px-1">
          <span className="text-[10px] text-stone-400 tabular-nums min-w-[32px]">
            {formatTime(currentTime)}
          </span>
          <input 
            type="range"
            min="0"
            max="100"
            step="0.1"
            value={progress * 100}
            onChange={(e) => seekByPercent(parseFloat(e.target.value))}
            className="flex-grow h-1 bg-stone-800 rounded-full appearance-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:rounded-full cursor-pointer"
          />
          <span className="text-[10px] text-stone-400 tabular-nums min-w-[32px] text-right">
            {formatTime(duration)}
          </span>
        </div>
      </div>
    </div>
  );
};
